import { Money } from '@palaccount/money';

import type { CartLine } from './cart';

/**
 * SAL-06's arithmetic on the CASHIER's side — the mirror of the server's
 * `apps/api/src/pos/till-math.ts`, function for function, rule for rule.
 *
 * Every amount a till screen shows or sends lives here, and every one of them
 * is a `Money` (decimal.js underneath). That is not tidiness, it is the fix
 * for a defect that reached the counter: the cart summary computed in JS
 * numbers, 22.10 at 15% floated to 3.31499…, `toFixed(2)` gave 3.31, and the
 * screen offered $25.41 against the server's $25.42 — a cent the cashier could
 * not account for at close of shift. The pieces that were still floats after
 * that fix — the change due, the split payment's card half (which is SENT to
 * the server, not merely drawn), the drawer difference and the refund's
 * derived unit price — are here too, for the same reason.
 *
 * This file is inside the `money/no-raw-money-arithmetic` fence in
 * `apps/web/eslint.config.js`; the page that draws these figures is not, so
 * the arithmetic has to be here to compile.
 *
 * One rule shapes the receipt (BR-SAL-06-04): the total is derived from the
 * ALREADY-ROUNDED tax, so the printed parts always add to the printed whole.
 */

/**
 * The ARITHMETIC TAG — the browser half of the server's `pos/till-math.ts`,
 * and pure for the same reason. It has no session to read a base currency
 * from and needs none: every figure on one receipt is in ONE money, and the
 * tag exists only because `Money` refuses to add across tags. It REACHES NO
 * COLUMN — the sale POSTS in `baseCurrencyCode(tx)` — and no label: the till
 * prints with `useBaseCurrency()`.
 */
const CUR = 'USD';
const money = (value: string): Money => Money.of(value, CUR);
const ZERO = money('0');

/**
 * Till inputs are free text in a box a cashier is still typing into, so a
 * blank or half-typed value must read as zero rather than throw.
 */
const DECIMAL_TEXT = /^-?\d+(\.\d+)?$/;

function moneyOrZero(raw: string | null | undefined): Money {
  const trimmed = (raw ?? '').trim();
  return DECIMAL_TEXT.test(trimmed) ? money(trimmed) : ZERO;
}

export interface CartSummary {
  subtotal: string;
  tax: string;
  total: string;
}

/** A line's net, floored at zero — the client half of BR-SAL-06-05. */
export function lineNetOf(line: Pick<CartLine, 'unitPrice' | 'qty' | 'lineDiscount'>): string {
  const gross = moneyOrZero(line.unitPrice)
    .times(String(line.qty))
    .minus(moneyOrZero(line.lineDiscount));
  return gross.isNegative() ? ZERO.toFixed2() : gross.toFixed2();
}

/**
 * The receipt as the cashier sees it. It mirrors the server's `cartTotals`
 * exactly — including deriving the total from the ALREADY-ROUNDED tax
 * (BR-SAL-06-04) — so the screen can never show a total the server will not
 * post.
 */
export function cartSummary(
  cart: readonly Pick<CartLine, 'unitPrice' | 'qty' | 'lineDiscount'>[],
  taxRate: string,
): CartSummary {
  const subtotal = cart.reduce((sum, line) => sum.plus(money(lineNetOf(line))), ZERO);
  // The total comes from the ROUNDED tax, so the parts always add to the whole.
  const tax = money(subtotal.times(taxRate).toFixed2());
  return {
    subtotal: subtotal.toFixed2(),
    tax: tax.toFixed2(),
    total: subtotal.plus(tax).toFixed2(),
  };
}

/**
 * BR-SAL-06-08: change is tendered minus total, shown BEFORE the sale
 * completes. Never negative on screen — a short tender is refused instead
 * (BR-SAL-06-07), which `isShortOnCash` answers.
 */
export function changeDue(tendered: string, total: string): string {
  const change = moneyOrZero(tendered).minus(moneyOrZero(total));
  return change.isNegative() ? ZERO.toFixed2() : change.toFixed2();
}

/** BR-SAL-06-07 — the sale cannot complete while the cash is short. */
export function isShortOnCash(tendered: string, total: string): boolean {
  return moneyOrZero(tendered).lessThan(moneyOrZero(total));
}

/**
 * The card half of a split payment: total minus the cash the cashier typed.
 *
 * This figure is not decoration — it is POSTED, as the card payment's amount.
 * A float here puts a cent into the books that nobody tendered.
 */
export function cardPart(total: string, cashPart: string): string {
  return moneyOrZero(total).minus(moneyOrZero(cashPart)).toFixed2();
}

/** §5h's close dialog: counted − expected. Negative is short. */
export function drawerDifference(counted: string, expected: string): string {
  return moneyOrZero(counted).minus(moneyOrZero(expected)).toFixed2();
}

/** True while a typed amount is worth sending — a zero leg is not a payment. */
export function isPayable(amount: string): boolean {
  return moneyOrZero(amount).isPositive();
}

/** True when a drawer count differs from what the drawer should hold. */
export function isDrawerOut(difference: string): boolean {
  return !moneyOrZero(difference).isZero();
}

/**
 * A refunded line's unit price, derived from the net the server recorded and
 * the quantity it covered: net ÷ qty.
 *
 * SAL-06 §5g refunds by line, and the refund total has to come out at the
 * figure the customer was charged. Dividing floats here was how the returns
 * pane drifted from the sale it was reversing.
 */
export function refundUnitPrice(lineNet: string, qty: string): string {
  const quantity = moneyOrZero(qty);
  if (quantity.isZero()) return ZERO.toFixed2();
  return moneyOrZero(lineNet).dividedBy(qty).toFixed2();
}
